/**
 * Brand kit snapshots for the playground's brand pages.
 *
 * The data is generated and committed by `pnpm --filter @tapp/design-tokens
 * generate:brand-kits`; nothing here calls Figma. Types mirror that generator's output
 * (packages/design-tokens/scripts/brand-kit-utils.ts) — they are restated rather than
 * imported because the generator's modules are build-time scripts, not package exports.
 *
 * See `stories.ts` for why playground-only helpers live in this directory.
 */

export type BrandKitColour = {
  name: string;
  hex: string;
  /** Null when the brand never filled it in and the kit still shows the template default. */
  cmyk: string | null;
  pms: string | null;
};

export type BrandKitTypeface = {
  /** `Primair lettertype`, `Systeemfont`, … as the brand kit labels it. */
  role: string;
  family: string;
  weights: string[];
};

export type BrandKitTypeStyle = {
  style: string;
  typeface: string;
  weight: string;
  note: string | null;
};

export type DriftMatch = 'exact' | 'near' | 'missing';

export type ColourDrift = {
  name: string;
  hex: string;
  match: DriftMatch;
  token: string | null;
  tokenHex: string | null;
  delta: number | null;
};

export type BrandKitAsset = { section: string; url: string };

export type BrandKit = {
  brand: string;
  title: string;
  tagline: string | null;
  figmaFileKey?: string;
  /** Null when the directory lists the brand but links no kit. */
  brandkitUrl: string | null;
  brandboxUrl?: string | null;
  colours?: BrandKitColour[];
  typography?: {
    typefaces: BrandKitTypeface[];
    webStyles: BrandKitTypeStyle[];
    printStyles: BrandKitTypeStyle[];
  };
  assets?: BrandKitAsset[];
  drift?: ColourDrift[];
};

const KIT_DIR = '../node_modules/@tapp/design-tokens/brand-kits';

// The glob patterns have to be literal strings — Vite rejects a template literal — so the
// directory is spelled out here even though KIT_DIR repeats it for the lookup below.
// index.json is the run's own summary, not a brand, and has a different shape.
const kitFiles = import.meta.glob<BrandKit>(
  ['../node_modules/@tapp/design-tokens/brand-kits/*.json', '!**/index.json'],
  { import: 'default' },
);

/** Brands that have a snapshot at all — a brand with no directory card has no file. */
export const brandsWithKit = new Set(
  Object.keys(kitFiles).map((path) => path.slice(KIT_DIR.length + 1, -'.json'.length)),
);

export async function loadBrandKit(brand: string) {
  return (await kitFiles[`${KIT_DIR}/${brand}.json`]?.()) ?? null;
}

/** Asset sections in the order the brand kit presents them, one row per Drive folder. */
export function groupAssets(assets: BrandKitAsset[] = []) {
  const sections = new Map<string, string[]>();

  for (const asset of assets) {
    sections.set(asset.section, [...(sections.get(asset.section) ?? []), asset.url]);
  }

  return [...sections].map(([section, urls]) => ({ section, urls }));
}
